/**
 * 24F Dashboard API Client
 * Handles all communication with the backend server
 */

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:24075/api';

// Token management
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('auth_token');
  }
  return authToken;
}

// API Response types
interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Supabase token getter (will be set from AuthContext)
let supabaseTokenGetter: (() => Promise<string | null>) | null = null;

export function setSupabaseTokenGetter(getter: () => Promise<string | null>) {
  supabaseTokenGetter = getter;
}

// Fetch wrapper with auth
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // Try to get Supabase token first, then fall back to stored auth token
  let token = getAuthToken();

  if (!token && supabaseTokenGetter) {
    token = await supabaseTokenGetter();
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true', // Skip ngrok warning page
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 401 - token expired (only redirect if not already on login page)
      if (response.status === 401 && !window.location.pathname.includes('/auth/')) {
        // Don't redirect, just throw error - let the component handle it
        console.warn('API returned 401 - authentication required');
      }
      // Create error with status code attached for permission checks
      const error = new Error(data.error || 'API request failed') as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// ============================================
// AUTH API
// ============================================
export const authApi = {
  // Get Google OAuth URL
  getGoogleAuthUrl: async () => {
    const response = await fetchApi<{ authUrl: string }>('/auth/google');
    return response.data?.authUrl;
  },

  // Dev login (localhost only)
  devLogin: async (email: string, password: string) => {
    const response = await fetchApi<{ token: string; user: any }>('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response.data?.token) {
      setAuthToken(response.data.token);
    }
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await fetchApi<{ user: any }>('/auth/me');
    return response.data?.user;
  },

  // Update profile
  updateProfile: async (data: { full_name?: string; phone?: string; role?: string }) => {
    const response = await fetchApi<{ user: any }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data?.user;
  },

  // Logout
  logout: async () => {
    await fetchApi('/auth/logout', { method: 'POST' });
    setAuthToken(null);
  },

  // Get Asana OAuth URL
  getAsanaAuthUrl: async () => {
    const response = await fetchApi<{ authUrl: string }>('/auth/asana');
    return response.data?.authUrl;
  },

  // Disconnect Asana
  disconnectAsana: async () => {
    return fetchApi('/auth/disconnect/asana', { method: 'POST' });
  },

  // Disconnect Google
  disconnectGoogle: async () => {
    return fetchApi('/auth/disconnect/google', { method: 'POST' });
  },
};

// ============================================
// CALENDAR API
// ============================================
export const calendarApi = {
  // Get calendar list
  getCalendars: async () => {
    const response = await fetchApi<{ calendars: any[] }>('/calendar/list');
    return response.data?.calendars || [];
  },

  // Get events
  getEvents: async (params?: { timeMin?: string; timeMax?: string; maxResults?: number }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ events: any[] }>(`/calendar/events${query}`);
    return response.data?.events || [];
  },

  // Get today's events
  getTodayEvents: async () => {
    const response = await fetchApi<{ events: any[] }>('/calendar/events/today');
    return response.data?.events || [];
  },

  // Get upcoming events
  getUpcomingEvents: async (days: number = 7) => {
    const response = await fetchApi<{ events: any[] }>(`/calendar/events/upcoming?days=${days}`);
    return response.data?.events || [];
  },

  // Create event
  createEvent: async (eventData: {
    summary: string;
    description?: string;
    startDateTime: string;
    endDateTime: string;
    attendees?: string[];
    location?: string;
    createMeetLink?: boolean;
  }) => {
    const response = await fetchApi<{ event: any }>('/calendar/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
    return response.data?.event;
  },

  // Update event
  updateEvent: async (eventId: string, eventData: any) => {
    const response = await fetchApi<{ event: any }>(`/calendar/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
    return response.data?.event;
  },

  // Delete event
  deleteEvent: async (eventId: string) => {
    return fetchApi(`/calendar/events/${eventId}`, { method: 'DELETE' });
  },

  // Check free/busy
  getFreeBusy: async (emails: string[], timeMin: string, timeMax: string) => {
    const response = await fetchApi<{ busyTimes: any }>('/calendar/freebusy', {
      method: 'POST',
      body: JSON.stringify({ emails, timeMin, timeMax }),
    });
    return response.data?.busyTimes;
  },
};

// ============================================
// GMAIL API
// ============================================
export const gmailApi = {
  // Get email profile
  getProfile: async () => {
    const response = await fetchApi<{ profile: any }>('/gmail/profile');
    return response.data?.profile;
  },

  // Get emails
  getEmails: async (params?: { maxResults?: number; query?: string; pageToken?: string }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ emails: any[]; nextPageToken?: string }>(`/gmail/messages${query}`);
    return response.data || { emails: [] };
  },

  // Get unread emails
  getUnreadEmails: async (maxResults: number = 20) => {
    const response = await fetchApi<{ emails: any[] }>(`/gmail/messages/unread?maxResults=${maxResults}`);
    return response.data?.emails || [];
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await fetchApi<{ count: number }>('/gmail/unread-count');
    return response.data?.count || 0;
  },

  // Get single email
  getEmail: async (messageId: string) => {
    const response = await fetchApi<{ email: any }>(`/gmail/messages/${messageId}`);
    return response.data?.email;
  },

  // Get thread
  getThread: async (threadId: string) => {
    const response = await fetchApi<{ emails: any[] }>(`/gmail/threads/${threadId}`);
    return response.data?.emails || [];
  },

  // Send email
  sendEmail: async (emailData: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    isHtml?: boolean;
    replyToMessageId?: string;
  }) => {
    const response = await fetchApi<{ id: string; threadId: string }>('/gmail/send', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
    return response.data;
  },

  // Mark as read
  markAsRead: async (messageId: string) => {
    return fetchApi(`/gmail/messages/${messageId}/read`, { method: 'POST' });
  },

  // Mark as unread
  markAsUnread: async (messageId: string) => {
    return fetchApi(`/gmail/messages/${messageId}/unread`, { method: 'POST' });
  },

  // Archive email
  archiveEmail: async (messageId: string) => {
    return fetchApi(`/gmail/messages/${messageId}/archive`, { method: 'POST' });
  },

  // Delete email
  deleteEmail: async (messageId: string) => {
    return fetchApi(`/gmail/messages/${messageId}`, { method: 'DELETE' });
  },

  // Search emails
  searchEmails: async (query: string, maxResults: number = 20) => {
    const response = await fetchApi<{ emails: any[] }>(`/gmail/search?q=${encodeURIComponent(query)}&maxResults=${maxResults}`);
    return response.data?.emails || [];
  },

  // Get labels
  getLabels: async () => {
    const response = await fetchApi<{ labels: any[] }>('/gmail/labels');
    return response.data?.labels || [];
  },
};

// ============================================
// ASANA API
// ============================================
export const asanaApi = {
  // Check connection status
  getStatus: async () => {
    const response = await fetchApi<{ connected: boolean }>('/asana/status');
    return response.data?.connected || false;
  },

  // Get current Asana user
  getCurrentUser: async () => {
    const response = await fetchApi<{ user: any }>('/asana/me');
    return response.data?.user;
  },

  // Get workspaces
  getWorkspaces: async () => {
    const response = await fetchApi<{ workspaces: any[] }>('/asana/workspaces');
    return response.data?.workspaces || [];
  },

  // Get projects
  getProjects: async (workspaceId: string) => {
    const response = await fetchApi<{ projects: any[] }>(`/asana/workspaces/${workspaceId}/projects`);
    return response.data?.projects || [];
  },

  // Get my tasks
  getMyTasks: async (workspaceId: string, completed?: boolean) => {
    const query = completed !== undefined ? `?completed=${completed}` : '';
    const response = await fetchApi<{ tasks: any[] }>(`/asana/workspaces/${workspaceId}/tasks/me${query}`);
    return response.data?.tasks || [];
  },

  // Get overdue tasks
  getOverdueTasks: async (workspaceId: string) => {
    const response = await fetchApi<{ tasks: any[] }>(`/asana/workspaces/${workspaceId}/tasks/overdue`);
    return response.data?.tasks || [];
  },

  // Get tasks due today
  getTasksDueToday: async (workspaceId: string) => {
    const response = await fetchApi<{ tasks: any[] }>(`/asana/workspaces/${workspaceId}/tasks/today`);
    return response.data?.tasks || [];
  },

  // Get project tasks
  getProjectTasks: async (projectId: string, completed?: boolean) => {
    const query = completed !== undefined ? `?completed=${completed}` : '';
    const response = await fetchApi<{ tasks: any[] }>(`/asana/projects/${projectId}/tasks${query}`);
    return response.data?.tasks || [];
  },

  // Get single task
  getTask: async (taskId: string) => {
    const response = await fetchApi<{ task: any }>(`/asana/tasks/${taskId}`);
    return response.data?.task;
  },

  // Create task
  createTask: async (workspaceId: string, taskData: {
    name: string;
    notes?: string;
    due_on?: string;
    projectId?: string;
    assigneeEmail?: string;
  }) => {
    const response = await fetchApi<{ task: any }>(`/asana/workspaces/${workspaceId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
    return response.data?.task;
  },

  // Update task
  updateTask: async (taskId: string, updates: any) => {
    const response = await fetchApi<{ task: any }>(`/asana/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data?.task;
  },

  // Complete task
  completeTask: async (taskId: string) => {
    return fetchApi(`/asana/tasks/${taskId}/complete`, { method: 'POST' });
  },

  // Delete task
  deleteTask: async (taskId: string) => {
    return fetchApi(`/asana/tasks/${taskId}`, { method: 'DELETE' });
  },

  // Search tasks
  searchTasks: async (workspaceId: string, query: string) => {
    const response = await fetchApi<{ tasks: any[] }>(`/asana/workspaces/${workspaceId}/tasks/search?q=${encodeURIComponent(query)}`);
    return response.data?.tasks || [];
  },

  // Get workspace users
  getWorkspaceUsers: async (workspaceId: string) => {
    const response = await fetchApi<{ users: any[] }>(`/asana/workspaces/${workspaceId}/users`);
    return response.data?.users || [];
  },
};

// ============================================
// MEETINGS API
// ============================================
export const meetingsApi = {
  // Get all meetings
  getMeetings: async (params?: { status?: string; from?: string; to?: string; limit?: number }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ meetings: any[] }>(`/meetings${query}`);
    return response.data?.meetings || [];
  },

  // Get today's meetings
  getTodayMeetings: async () => {
    const response = await fetchApi<{ meetings: any[] }>('/meetings/today');
    return response.data?.meetings || [];
  },

  // Get upcoming meetings
  getUpcomingMeetings: async (days: number = 7) => {
    const response = await fetchApi<{ meetings: any[] }>(`/meetings/upcoming?days=${days}`);
    return response.data?.meetings || [];
  },

  // Get single meeting
  getMeeting: async (meetingId: string) => {
    const response = await fetchApi<{ meeting: any }>(`/meetings/${meetingId}`);
    return response.data?.meeting;
  },

  // Create meeting
  createMeeting: async (meetingData: {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    attendeeEmails: string[];
    createCalendarEvent?: boolean;
    createMeetLink?: boolean;
  }) => {
    const response = await fetchApi<{ meeting: any }>('/meetings', {
      method: 'POST',
      body: JSON.stringify(meetingData),
    });
    return response.data?.meeting;
  },

  // Update meeting
  updateMeeting: async (meetingId: string, updates: any) => {
    const response = await fetchApi<{ meeting: any }>(`/meetings/${meetingId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data?.meeting;
  },

  // Cancel meeting
  cancelMeeting: async (meetingId: string) => {
    return fetchApi(`/meetings/${meetingId}/cancel`, { method: 'POST' });
  },

  // Respond to meeting
  respondToMeeting: async (meetingId: string, response: 'accepted' | 'declined' | 'tentative') => {
    return fetchApi(`/meetings/${meetingId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    });
  },

  // Delete meeting
  deleteMeeting: async (meetingId: string) => {
    return fetchApi(`/meetings/${meetingId}`, { method: 'DELETE' });
  },

  // Get available team members
  getTeamMembers: async () => {
    const response = await fetchApi<{ users: any[] }>('/meetings/team/available');
    return response.data?.users || [];
  },
};

// ============================================
// DASHBOARD API
// ============================================
export const dashboardApi = {
  // Get full dashboard data (legacy - use individual endpoints for better performance)
  getDashboardData: async () => {
    const response = await fetchApi<any>('/dashboard');
    return response.data;
  },

  // Get calendar summary (with server-side caching)
  getCalendarSummary: async () => {
    const response = await fetchApi<any>('/dashboard/calendar');
    return response.data;
  },

  // Get email summary (with server-side caching)
  getEmailSummary: async () => {
    const response = await fetchApi<any>('/dashboard/emails');
    return response.data;
  },

  // Get Asana summary (with server-side caching)
  getAsanaSummary: async () => {
    const response = await fetchApi<any>('/dashboard/asana');
    return response.data;
  },

  // Get meetings summary (with server-side caching)
  getMeetingsSummary: async () => {
    const response = await fetchApi<any>('/dashboard/meetings');
    return response.data;
  },

  // Get stats (with server-side caching)
  getStats: async () => {
    const response = await fetchApi<any>('/dashboard/stats');
    return response.data;
  },

  // Get all dashboard data in parallel (faster than single endpoint)
  getAllParallel: async () => {
    const [calendar, emails, asana, meetings] = await Promise.allSettled([
      dashboardApi.getCalendarSummary(),
      dashboardApi.getEmailSummary(),
      dashboardApi.getAsanaSummary(),
      dashboardApi.getMeetingsSummary(),
    ]);

    return {
      calendar: calendar.status === 'fulfilled' ? calendar.value : null,
      emails: emails.status === 'fulfilled' ? emails.value : null,
      asana: asana.status === 'fulfilled' ? asana.value : null,
      meetings: meetings.status === 'fulfilled' ? meetings.value : null,
    };
  },
};

// ============================================
// VOICE API (ElevenLabs Integration)
// ============================================
export const voiceApi = {
  // Get available voices
  getVoices: async () => {
    const response = await fetchApi<{ voices: any[] }>('/voice/voices');
    return response.data?.voices || [];
  },

  // Get a specific voice
  getVoice: async (voiceId: string) => {
    const response = await fetchApi<{ voice: any }>(`/voice/voices/${voiceId}`);
    return response.data?.voice;
  },

  // Delete a voice
  deleteVoice: async (voiceId: string) => {
    return fetchApi(`/voice/voices/${voiceId}`, { method: 'DELETE' });
  },

  // Edit voice details
  editVoice: async (voiceId: string, updates: { name?: string; description?: string; labels?: Record<string, string> }) => {
    return fetchApi(`/voice/voices/${voiceId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Get voice settings
  getVoiceSettings: async (voiceId: string) => {
    const response = await fetchApi<{ settings: any }>(`/voice/voices/${voiceId}/settings`);
    return response.data?.settings;
  },

  // Edit voice settings
  editVoiceSettings: async (voiceId: string, settings: {
    stability: number;
    similarity_boost: number;
    style?: number;
    use_speaker_boost?: boolean;
  }) => {
    return fetchApi(`/voice/voices/${voiceId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  // Clone voice (instant voice cloning)
  cloneVoice: async (data: {
    name: string;
    description?: string;
    labels?: Record<string, string>;
    audioBase64: string[];
  }) => {
    const response = await fetchApi<{ voice: any }>('/voice/voices/clone', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data?.voice;
  },

  // Get voice models
  getModels: async () => {
    const response = await fetchApi<{ models: any[] }>('/voice/models');
    return response.data?.models || [];
  },

  // Get models with full details
  getModelsFull: async () => {
    const response = await fetchApi<{ models: any[] }>('/voice/models/full');
    return response.data?.models || [];
  },

  // Text to speech (returns audio blob URL)
  textToSpeech: async (text: string, voiceId: string, settings?: any): Promise<string> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/voice/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text, voiceId, settings }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate speech');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  // Get subscription info (basic)
  getSubscription: async () => {
    const response = await fetchApi<{ subscription: any }>('/voice/subscription');
    return response.data?.subscription;
  },

  // Get full subscription details (credits, limits, billing)
  getSubscriptionFull: async () => {
    const response = await fetchApi<{ subscription: any }>('/voice/subscription/full');
    return response.data?.subscription;
  },

  // Get generation history (basic)
  getHistory: async (pageSize?: number) => {
    const query = pageSize ? `?pageSize=${pageSize}` : '';
    const response = await fetchApi<{ history: any[] }>(`/voice/history${query}`);
    return response.data?.history || [];
  },

  // Get generation history with full details and pagination
  getHistoryFull: async (options?: {
    page_size?: number;
    start_after_history_item_id?: string;
    voice_id?: string;
    source?: string;
  }) => {
    const query = options ? '?' + new URLSearchParams(options as any).toString() : '';
    const response = await fetchApi<{ history: any[]; has_more: boolean; last_history_item_id?: string }>(`/voice/history/full${query}`);
    return response.data;
  },

  // Delete history item
  deleteHistoryItem: async (historyItemId: string) => {
    return fetchApi(`/voice/history/${historyItemId}`, { method: 'DELETE' });
  },

  // Get history audio (returns audio blob URL)
  getHistoryAudio: async (historyItemId: string): Promise<string> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/voice/history/${historyItemId}/audio`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get audio');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  // Get history audio as base64
  getHistoryAudioBase64: async (historyItemId: string) => {
    const response = await fetchApi<{ audio: string }>(`/voice/history/${historyItemId}/audio/base64`);
    return response.data?.audio;
  },

  // Get voice agents
  getAgents: async () => {
    const response = await fetchApi<{ agents: any[] }>('/voice/agents');
    return response.data?.agents || [];
  },

  // Get single agent
  getAgent: async (agentId: string) => {
    const response = await fetchApi<{ agent: any }>(`/voice/agents/${agentId}`);
    return response.data?.agent;
  },

  // Create agent
  createAgent: async (agentData: {
    name: string;
    voice_id: string;
    description?: string;
    system_prompt?: string;
    greeting_message?: string;
    settings?: any;
  }) => {
    const response = await fetchApi<{ agent: any }>('/voice/agents', {
      method: 'POST',
      body: JSON.stringify(agentData),
    });
    return response.data?.agent;
  },

  // Update agent
  updateAgent: async (agentId: string, updates: any) => {
    const response = await fetchApi<{ agent: any }>(`/voice/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data?.agent;
  },

  // Delete agent
  deleteAgent: async (agentId: string) => {
    return fetchApi(`/voice/agents/${agentId}`, { method: 'DELETE' });
  },

  // Toggle agent active status
  toggleAgent: async (agentId: string) => {
    const response = await fetchApi<{ agent: any }>(`/voice/agents/${agentId}/toggle`, {
      method: 'POST',
    });
    return response.data?.agent;
  },

  // Test agent (returns audio blob URL)
  testAgent: async (agentId: string): Promise<string> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/voice/agents/${agentId}/test`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to test agent');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};

// ============================================
// AI API
// ============================================
export const aiApi = {
  // Chat completion
  chat: async (messages: { role: string; content: string }[], options?: {
    model?: string;
    temperature?: number;
    max_tokens?: number;
  }) => {
    const response = await fetchApi<{ content: string; usage: any }>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, ...options }),
    });
    return response.data;
  },

  // Summarize text
  summarize: async (text: string, options?: { maxLength?: number; style?: string }) => {
    const response = await fetchApi<{ summary: string }>('/ai/summarize', {
      method: 'POST',
      body: JSON.stringify({ text, ...options }),
    });
    return response.data?.summary;
  },

  // Analyze sentiment
  analyzeSentiment: async (text: string) => {
    const response = await fetchApi<{ sentiment: any }>('/ai/sentiment', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
    return response.data?.sentiment;
  },

  // Generate email draft
  generateEmail: async (context: {
    purpose: string;
    recipient?: string;
    tone?: string;
    keyPoints?: string[];
  }) => {
    const response = await fetchApi<{ email: { subject: string; body: string } }>('/ai/generate-email', {
      method: 'POST',
      body: JSON.stringify(context),
    });
    return response.data?.email;
  },

  // Generate meeting summary
  generateMeetingSummary: async (transcript: string, options?: {
    includeActionItems?: boolean;
    includeDecisions?: boolean;
  }) => {
    const response = await fetchApi<{ summary: any }>('/ai/meeting-summary', {
      method: 'POST',
      body: JSON.stringify({ transcript, ...options }),
    });
    return response.data?.summary;
  },

  // Get available models
  getModels: async () => {
    const response = await fetchApi<{ models: any[] }>('/ai/models');
    return response.data?.models || [];
  },
};

// ============================================
// SETTINGS API (Super Admin only)
// ============================================
export const settingsApi = {
  // Get all settings
  getSettings: async () => {
    const response = await fetchApi<{ settings: any[] }>('/settings');
    return response.data?.settings || [];
  },

  // Get single setting
  getSetting: async (key: string) => {
    const response = await fetchApi<{ key: string; value: string }>(`/settings/${key}`);
    return response.data;
  },

  // Set setting
  setSetting: async (key: string, value: string, options?: {
    description?: string;
    isEncrypted?: boolean;
  }) => {
    const response = await fetchApi<{ setting: any }>('/settings', {
      method: 'POST',
      body: JSON.stringify({ key, value, ...options }),
    });
    return response.data?.setting;
  },

  // Delete setting
  deleteSetting: async (key: string) => {
    return fetchApi(`/settings/${key}`, { method: 'DELETE' });
  },

  // Get available setting keys
  getAvailableKeys: async () => {
    const response = await fetchApi<{ keys: any[] }>('/settings/keys/available');
    return response.data?.keys || [];
  },

  // Batch update settings
  batchUpdate: async (settings: { key: string; value: string; description?: string; isEncrypted?: boolean }[]) => {
    const response = await fetchApi<any>('/settings/batch', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    });
    return response.data;
  },
};

// ============================================
// USERS API
// ============================================
export const usersApi = {
  // Get all users (admin only)
  getUsers: async (params?: { role?: string; search?: string; limit?: number; offset?: number }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ users: any[]; total: number }>(`/users${query}`);
    return response.data || { users: [], total: 0 };
  },

  // Get single user
  getUser: async (userId: string) => {
    const response = await fetchApi<{ user: any }>(`/users/${userId}`);
    return response.data?.user;
  },

  // Update user
  updateUser: async (userId: string, updates: any) => {
    const response = await fetchApi<{ user: any }>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data?.user;
  },

  // Delete user (super admin only)
  deleteUser: async (userId: string) => {
    return fetchApi(`/users/${userId}`, { method: 'DELETE' });
  },

  // Set user role
  setUserRole: async (userId: string, role: string) => {
    const response = await fetchApi<{ user: any }>(`/users/${userId}/role`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
    return response.data?.user;
  },

  // Toggle super admin
  toggleSuperAdmin: async (userId: string) => {
    const response = await fetchApi<{ user: any }>(`/users/${userId}/super-admin`, {
      method: 'POST',
    });
    return response.data?.user;
  },

  // Get users by role
  getUsersByRole: async (role: string) => {
    const response = await fetchApi<{ users: any[] }>(`/users/by-role/${role}`);
    return response.data?.users || [];
  },
};

// ============================================
// SMART EMAIL API
// ============================================
export const smartEmailApi = {
  // Smart search with filters
  search: async (params: {
    userId?: string;
    filter?: 'unread' | 'starred' | 'important' | 'sent' | 'drafts' | 'spam' | 'trash';
    from?: string;
    to?: string;
    subject?: string;
    hasAttachment?: boolean;
    after?: string;
    before?: string;
    label?: string;
    q?: string;
    limit?: number;
  } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, String(value));
    });
    const response = await fetchApi<any>(`/smart-email/search?${query}`);
    return response.data;
  },

  // Get email stats
  getStats: async (userId?: string) => {
    const query = userId ? `?userId=${userId}` : '';
    const response = await fetchApi<any>(`/smart-email/stats${query}`);
    return response.data;
  },

  // Get emails by category
  getByCategory: async (category: 'primary' | 'social' | 'promotions' | 'updates' | 'forums', userId?: string) => {
    const query = userId ? `?userId=${userId}` : '';
    const response = await fetchApi<any>(`/smart-email/category/${category}${query}`);
    return response.data;
  },

  // Send email (optionally on behalf of another user)
  send: async (data: {
    userId?: string;
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    isHtml?: boolean;
    replyToMessageId?: string;
  }) => {
    const response = await fetchApi<any>('/smart-email/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Bulk operations
  bulk: async (data: {
    messageIds: string[];
    action: 'markRead' | 'markUnread' | 'archive' | 'delete';
    userId?: string;
  }) => {
    const response = await fetchApi<any>('/smart-email/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // AI summarize email
  summarize: async (messageId: string, userId?: string) => {
    const response = await fetchApi<any>('/smart-email/summarize', {
      method: 'POST',
      body: JSON.stringify({ messageId, userId }),
    });
    return response.data;
  },

  // AI generate reply
  generateReply: async (messageId: string, tone?: string, instructions?: string, userId?: string) => {
    const response = await fetchApi<any>('/smart-email/generate-reply', {
      method: 'POST',
      body: JSON.stringify({ messageId, tone, instructions, userId }),
    });
    return response.data;
  },

  // AI compose new email
  aiCompose: async (prompt: string, language: 'en' | 'ar', recipients?: string[]) => {
    const response = await fetchApi<any>('/smart-email/compose', {
      method: 'POST',
      body: JSON.stringify({ prompt, language, recipients }),
    });
    return response.data;
  },

  // AI edit email text
  aiEdit: async (text: string, instruction: string, language: 'en' | 'ar') => {
    const response = await fetchApi<any>('/smart-email/edit', {
      method: 'POST',
      body: JSON.stringify({ text, instruction, language }),
    });
    return response.data;
  },

  // Admin: Get all users email overview
  getAdminOverview: async () => {
    const response = await fetchApi<any>('/smart-email/admin/overview');
    return response.data;
  },

  // Admin: Get specific user's emails
  getUserEmails: async (userId: string, filter?: string) => {
    const query = filter ? `?filter=${filter}` : '';
    const response = await fetchApi<any>(`/smart-email/admin/user/${userId}/emails${query}`);
    return response.data;
  },
};

// ============================================
// SMART MEETINGS API
// ============================================
export const smartMeetingsApi = {
  // Smart search with filters
  search: async (params: {
    userId?: string;
    status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    attendee?: string;
    from?: string;
    to?: string;
    hasLink?: boolean;
    organizedBy?: string;
    limit?: number;
  } = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, String(value));
    });
    const response = await fetchApi<any>(`/smart-meetings/search?${query}`);
    return response.data;
  },

  // Get meeting stats
  getStats: async (userId?: string, period?: 'today' | 'week' | 'month' | 'year') => {
    const query = new URLSearchParams();
    if (userId) query.append('userId', userId);
    if (period) query.append('period', period);
    const response = await fetchApi<any>(`/smart-meetings/stats?${query}`);
    return response.data;
  },

  // Create meeting with conflict detection
  create: async (data: {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    attendeeEmails: string[];
    createCalendarEvent?: boolean;
    createMeetLink?: boolean;
    sendInvites?: boolean;
    checkConflicts?: boolean;
  }) => {
    const response = await fetchApi<any>('/smart-meetings/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Find available time slots
  findSlots: async (data: {
    attendeeEmails: string[];
    date?: string;
    duration?: number;
    workingHoursStart?: string;
    workingHoursEnd?: string;
  }) => {
    const response = await fetchApi<any>('/smart-meetings/find-slots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Generate meeting summary
  generateSummary: async (meetingId?: string, transcript?: string, notes?: string) => {
    const response = await fetchApi<any>('/smart-meetings/generate-summary', {
      method: 'POST',
      body: JSON.stringify({ meetingId, transcript, notes }),
    });
    return response.data;
  },

  // Admin: Get all meetings overview
  getAdminOverview: async (from?: string, to?: string) => {
    const query = new URLSearchParams();
    if (from) query.append('from', from);
    if (to) query.append('to', to);
    const response = await fetchApi<any>(`/smart-meetings/admin/overview?${query}`);
    return response.data;
  },

  // Send meeting reminder
  sendReminder: async (meetingId: string, message?: string) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}/remind`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response;
  },

  // List all meetings
  list: async (params: { limit?: number; status?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.limit) query.append('limit', String(params.limit));
    if (params.status) query.append('status', params.status);
    const response = await fetchApi<any>(`/smart-meetings/list?${query}`);
    return response.data;
  },

  // Cancel a meeting
  cancel: async (meetingId: string) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}/cancel`, {
      method: 'POST',
    });
    return response;
  },

  // Update meeting status
  updateStatus: async (meetingId: string, status: string) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    return response;
  },

  // Update meeting details
  update: async (meetingId: string, data: {
    title?: string;
    description?: string;
    attendeeEmails?: string[];
  }) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  // Add attendee to meeting
  addAttendee: async (meetingId: string, email: string) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}/attendees`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response;
  },

  // Remove attendee from meeting
  removeAttendee: async (meetingId: string, email: string) => {
    const response = await fetchApi<any>(`/smart-meetings/${meetingId}/attendees`, {
      method: 'DELETE',
      body: JSON.stringify({ email }),
    });
    return response;
  },
};

// ============================================
// USER PREFERENCES API
// ============================================
export const preferencesApi = {
  // Get user preferences
  getPreferences: async () => {
    const response = await fetchApi<{ preferences: any }>('/preferences');
    return response.data?.preferences;
  },

  // Update user preferences
  updatePreferences: async (updates: {
    language?: string;
    theme?: string;
    email_notifications?: boolean;
    push_notifications?: boolean;
    ai_language?: string;
    date_format?: string;
    time_format?: string;
    timezone?: string;
  }) => {
    const response = await fetchApi<{ preferences: any }>('/preferences', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data?.preferences;
  },

  // Get available languages
  getLanguages: async () => {
    const response = await fetchApi<{ languages: any[] }>('/preferences/languages');
    return response.data?.languages || [];
  },

  // Get available themes
  getThemes: async () => {
    const response = await fetchApi<{ themes: any[] }>('/preferences/themes');
    return response.data?.themes || [];
  },

  // Get available timezones
  getTimezones: async () => {
    const response = await fetchApi<{ timezones: any[] }>('/preferences/timezones');
    return response.data?.timezones || [];
  },
};

// ============================================
// AI AGENT API
// ============================================
export const agentApi = {
  // Send message to AI agent (non-streaming) with images
  chat: async (message: string, conversationId?: string | null, history?: any[], images?: string[]) => {
    const response = await fetchApi<{
      response: string;
      toolCalls?: { name: string; result: any }[];
      conversationId: string;
    }>('/agent/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationId, history, images: images || [] }),
    });
    return response.data!;
  },

  // Send message to AI agent with streaming (typing effect)
  chatStream: async (
    message: string,
    conversationId: string | null,
    history: any[],
    images?: string[], // Array of base64 image strings
    onText?: (text: string) => void,
    onToolStart?: (tool: { name: string; args: any }) => void,
    onToolEnd?: (tool: { name: string; result: any }) => void,
    onDone?: (data: { conversationId: string }) => void,
    onError?: (error: string) => void
  ) => {
    let token = getAuthToken();
    if (!token && supabaseTokenGetter) {
      token = await supabaseTokenGetter();
    }

    try {
      const response = await fetch(`${API_BASE_URL}/agent/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ message, conversationId, history, images: images || [] }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Stream request failed');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (data.type) {
                case 'text':
                  onText?.(data.content);
                  break;
                case 'tool_start':
                  onToolStart?.(data.tool);
                  break;
                case 'tool_end':
                  onToolEnd?.(data.tool);
                  break;
                case 'done':
                  onDone?.({ conversationId: data.conversationId });
                  break;
                case 'error':
                  onError?.(data.error);
                  break;
              }
            } catch (e) {
              // Ignore JSON parse errors
            }
          }
        }
      }
    } catch (error: any) {
      onError?.(error.message || 'Stream failed');
    }
  },

  // Get all conversations
  getConversations: async () => {
    const response = await fetchApi<any[]>('/agent/conversations');
    return response.data || [];
  },

  // Get single conversation
  getConversation: async (conversationId: string) => {
    const response = await fetchApi<any>(`/agent/conversations/${conversationId}`);
    return response.data;
  },

  // Delete conversation
  deleteConversation: async (conversationId: string) => {
    return fetchApi(`/agent/conversations/${conversationId}`, { method: 'DELETE' });
  },

  // Clear conversation
  clearConversation: async (conversationId: string) => {
    return fetchApi(`/agent/conversations/${conversationId}/clear`, { method: 'POST' });
  },

  // Get generated voiceover audio by ID
  getAudio: async (audioId: string): Promise<string | null> => {
    const response = await fetchApi<{ audioUrl: string }>(`/agent/audio/${audioId}`);
    return response.data?.audioUrl || null;
  },

  // Get agent configuration (model, etc.)
  getConfig: async (): Promise<{ model: string }> => {
    const response = await fetchApi<{ model: string }>('/agent/config');
    return response.data || { model: 'gpt-4o-mini' };
  },
};

// ============================================
// WHATSAPP BUSINESS API (Twilio)
// ============================================

// WhatsApp Bot interface
export interface WhatsAppBot {
  id: string;
  name: string;
  description?: string;
  account_sid: string;
  auth_token: string;
  twilio_phone_number: string;
  display_phone_number?: string;
  verified_name?: string;
  is_active: boolean;
  is_api_verified: boolean;
  created_at: string;
  updated_at: string;
}

export const whatsappApi = {
  // Get all bots
  getBots: async (): Promise<WhatsAppBot[]> => {
    const response = await fetchApi<WhatsAppBot[]>('/whatsapp/bots');
    return response.data || [];
  },

  // Get a specific bot
  getBot: async (botId: string): Promise<WhatsAppBot | null> => {
    const response = await fetchApi<WhatsAppBot>(`/whatsapp/bots/${botId}`);
    return response.data || null;
  },

  // Create a new bot
  createBot: async (data: {
    name: string;
    description?: string;
    accountSid: string;
    authToken: string;
    twilioPhoneNumber: string;
  }): Promise<WhatsAppBot | null> => {
    const response = await fetchApi<WhatsAppBot>('/whatsapp/bots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data || null;
  },

  // Update a bot
  updateBot: async (botId: string, data: {
    name?: string;
    description?: string;
    accountSid?: string;
    authToken?: string;
    twilioPhoneNumber?: string;
    isActive?: boolean;
  }): Promise<WhatsAppBot | null> => {
    const response = await fetchApi<WhatsAppBot>(`/whatsapp/bots/${botId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data || null;
  },

  // Delete a bot
  deleteBot: async (botId: string): Promise<boolean> => {
    const response = await fetchApi(`/whatsapp/bots/${botId}`, {
      method: 'DELETE',
    });
    return response.success;
  },

  // Verify a specific bot's connection
  verifyBot: async (botId: string): Promise<{
    success: boolean;
    phoneNumber?: string;
    displayName?: string;
    error?: string;
  }> => {
    const response = await fetchApi<{
      phoneNumber?: string;
      displayName?: string;
    }>(`/whatsapp/bots/${botId}/verify`, { method: 'POST' });
    return {
      success: response.success,
      phoneNumber: response.data?.phoneNumber,
      displayName: response.data?.displayName,
      error: response.error,
    };
  },

  // Send test message using a specific bot
  sendBotTestMessage: async (botId: string, phoneNumber: string, message: string) => {
    return fetchApi(`/whatsapp/bots/${botId}/test`, {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, message }),
    });
  },

  // Get webhook URL for a specific bot
  getBotWebhookUrl: async (botId: string): Promise<{
    webhookUrl: string;
  } | null> => {
    const response = await fetchApi<{
      webhookUrl: string;
    }>(`/whatsapp/bots/${botId}/webhook-url`);
    return response.data || null;
  },

  // Setup/check whatsapp_bots table
  setupTable: async (): Promise<{
    success: boolean;
    message: string;
    sql?: string;
  }> => {
    const response = await fetchApi<any>('/whatsapp/setup-table', { method: 'POST' });
    return {
      success: response.success,
      message: (response as any).message || '',
      sql: (response as any).sql,
    };
  },
};

// ============================================
// AGENT ANALYTICS API
// ============================================
export const agentAnalyticsApi = {
  // Get overall statistics
  getStats: async () => {
    const response = await fetchApi<{
      totalChats: number;
      totalToolCalls: number;
      totalVoiceOvers: number;
      totalErrors: number;
      totalUsers: number;
      webUsage: number;
      whatsappUsage: number;
    }>('/agent-analytics/stats');
    return response.data;
  },

  // Get top users by usage
  getTopUsers: async (limit: number = 10) => {
    const response = await fetchApi<Array<{
      userId: string;
      fullName: string;
      email: string;
      totalChats: number;
      totalToolCalls: number;
      totalVoiceOvers: number;
      totalErrors: number;
      lastActivity: string;
    }>>(`/agent-analytics/top-users?limit=${limit}`);
    return response.data;
  },

  // Get tool usage statistics
  getToolStats: async () => {
    const response = await fetchApi<Array<{
      toolName: string;
      totalCalls: number;
      successRate: number;
      lastUsed: string;
    }>>('/agent-analytics/tools');
    return response.data;
  },

  // Get daily statistics
  getDailyStats: async (days: number = 30) => {
    const response = await fetchApi<Array<{
      date: string;
      chats: number;
      toolCalls: number;
      voiceOvers: number;
      errors: number;
    }>>(`/agent-analytics/daily?days=${days}`);
    return response.data;
  },

  // Get recent logs
  getRecentLogs: async (limit: number = 50) => {
    const response = await fetchApi<Array<{
      id: string;
      user_id: string;
      action_type: string;
      tool_name: string | null;
      details: Record<string, any>;
      source: string;
      created_at: string;
      users: { full_name: string; email: string };
    }>>(`/agent-analytics/logs?limit=${limit}`);
    return response.data;
  },

  // Get time savings statistics
  getTimeSavings: async (days: number = 30) => {
    const response = await fetchApi<{
      totalTimeSavedSeconds: number;
      totalTimeSavedFormatted: string;
      totalManualTimeSeconds: number;
      totalAiTimeSeconds: number;
      efficiencyPercentage: number;
      byTool: Array<{
        toolName: string;
        label: string;
        count: number;
        timeSavedSeconds: number;
        manualTimeSeconds: number;
        aiTimeSeconds: number;
      }>;
      byPeriod: {
        today: number;
        week: number;
        month: number;
        allTime: number;
      };
      dailyBreakdown: Array<{
        date: string;
        timeSavedSeconds: number;
        toolCalls: number;
      }>;
    }>(`/agent-analytics/time-savings?days=${days}`);
    return response.data;
  },
};

// ============================================
// ZOHO PEOPLE API (HR - Attendance & Leave)
// ============================================
export const zohoApi = {
  // Check Zoho connection status
  getStatus: async () => {
    const response = await fetchApi<{ connected: boolean; message: string }>('/zoho/status');
    return response.data || { connected: false, message: 'Unknown' };
  },

  // Get Zoho OAuth URL
  getAuthUrl: async () => {
    const response = await fetchApi<{ authUrl: string }>('/zoho/auth/url');
    return response.data?.authUrl;
  },

  // Get current employee info
  getMe: async () => {
    const response = await fetchApi<{ employee: any }>('/zoho/me');
    return response.data?.employee;
  },

  // Get my reporting manager
  getManager: async () => {
    const response = await fetchApi<{ manager: any }>('/zoho/me/manager');
    return response.data?.manager;
  },

  // ============= Attendance =============

  // Get attendance records for date range
  getAttendance: async (from: string, to: string) => {
    const response = await fetchApi<{ records: any[] }>(`/zoho/attendance?from=${from}&to=${to}`);
    return response.data?.records || [];
  },

  // Get attendance summary for a month
  getAttendanceSummary: async (month?: number, year?: number) => {
    const query = new URLSearchParams();
    if (month) query.append('month', month.toString());
    if (year) query.append('year', year.toString());
    const response = await fetchApi<{ summary: any }>(`/zoho/attendance/summary?${query}`);
    return response.data?.summary;
  },

  // Get today's attendance
  getTodayAttendance: async () => {
    const response = await fetchApi<{ attendance: any }>('/zoho/attendance/today');
    return response.data?.attendance;
  },

  // ============= Leave =============

  // Get leave types
  getLeaveTypes: async () => {
    const response = await fetchApi<{ leaveTypes: any[] }>('/zoho/leave/types');
    return response.data?.leaveTypes || [];
  },

  // Get leave balance
  getLeaveBalance: async () => {
    const response = await fetchApi<{ balances: any[] }>('/zoho/leave/balance');
    return response.data?.balances || [];
  },

  // Get leave requests
  getLeaveRequests: async (status?: 'Pending' | 'Approved' | 'Rejected' | 'All') => {
    const query = status ? `?status=${status}` : '';
    const response = await fetchApi<{ requests: any[] }>(`/zoho/leave/requests${query}`);
    return response.data?.requests || [];
  },

  // Apply for leave
  applyLeave: async (data: {
    leaveTypeId: string;
    from: string;
    to: string;
    reason?: string;
    dayType?: 'Full Day' | 'First Half' | 'Second Half';
  }) => {
    const response = await fetchApi<{ leaveId: string; message: string }>('/zoho/leave/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Cancel leave request
  cancelLeave: async (leaveId: string) => {
    return fetchApi(`/zoho/leave/${leaveId}/cancel`, { method: 'POST' });
  },

  // ============= Manager/Admin Functions =============

  // Get pending leave approvals (for managers)
  getPendingApprovals: async () => {
    const response = await fetchApi<{ approvals: any[] }>('/zoho/leave/pending-approvals');
    return response.data?.approvals || [];
  },

  // Approve leave request
  approveLeave: async (leaveId: string) => {
    return fetchApi(`/zoho/leave/${leaveId}/approve`, { method: 'POST' });
  },

  // Reject leave request
  rejectLeave: async (leaveId: string, reason?: string) => {
    return fetchApi(`/zoho/leave/${leaveId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // Get team attendance (for managers)
  getTeamAttendance: async (date?: string) => {
    const query = date ? `?date=${date}` : '';
    const response = await fetchApi<{ teamAttendance: any }>(`/zoho/team/attendance${query}`);
    return response.data?.teamAttendance;
  },

  // Get all employees attendance (admin only)
  getAllAttendance: async (date?: string) => {
    const query = date ? `?date=${date}` : '';
    const response = await fetchApi<{ attendance: any }>(`/zoho/admin/attendance${query}`);
    return response.data?.attendance;
  },

  // ============= Holidays =============

  // Get holidays for a year
  getHolidays: async (year?: number) => {
    const query = year ? `?year=${year}` : '';
    const response = await fetchApi<{ holidays: any[] }>(`/zoho/holidays${query}`);
    return response.data?.holidays || [];
  },

  // ============= Settings (Admin) =============

  // Get Zoho settings
  getSettings: async () => {
    const response = await fetchApi<{
      configured: boolean;
      organizationId: string | null;
      domain: string;
      authenticated: boolean;
    }>('/zoho/settings');
    return response.data;
  },

  // Save Zoho settings
  saveSettings: async (data: {
    clientId: string;
    clientSecret: string;
    organizationId?: string;
    domain?: string;
  }) => {
    return fetchApi('/zoho/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Disconnect Zoho (clear tokens to reconnect)
  disconnect: async () => {
    return fetchApi('/zoho/disconnect', { method: 'POST' });
  },
};

// ============================================
// RENDER API (Placid Image Generation)
// ============================================
export const renderApi = {
  // Test Placid API connection
  testConnection: async () => {
    const response = await fetchApi<{ success: boolean; message: string }>('/render/test-connection');
    return response.data || { success: false, message: 'Unknown error' };
  },

  // Get all templates
  getTemplates: async (params?: {
    collection_id?: string;
    title_filter?: string;
    order_by?: 'created_at' | 'title';
    page?: number;
  }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ templates: any[]; total: number; page: number }>(`/render/templates${query}`);
    return response.data || { templates: [], total: 0, page: 1 };
  },

  // Get single template with layer info
  getTemplate: async (templateId: string) => {
    const response = await fetchApi<{ template: any }>(`/render/templates/${templateId}`);
    return response.data?.template;
  },

  // Create new template
  createTemplate: async (data: {
    title: string;
    width: number;
    height: number;
    tags?: string[];
    from_template?: string;
  }) => {
    const response = await fetchApi<{ template: any }>('/render/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data?.template;
  },

  // Update template
  updateTemplate: async (templateId: string, updates: {
    title?: string;
    tags?: string[];
    custom_data?: Record<string, any>;
  }) => {
    const response = await fetchApi<{ template: any }>(`/render/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data?.template;
  },

  // Delete template
  deleteTemplate: async (templateId: string) => {
    return fetchApi(`/render/templates/${templateId}`, { method: 'DELETE' });
  },

  // Generate image from template
  generateImage: async (data: {
    template_uuid: string;
    layers: Record<string, {
      text?: string;
      image?: string;
      hide?: boolean;
      color?: string;
    }>;
    modifications?: {
      width?: number;
      height?: number;
      filename?: string;
      image_format?: 'jpg' | 'png' | 'webp';
    };
    create_now?: boolean;
  }) => {
    const response = await fetchApi<{
      id: string;
      status: 'queued' | 'finished' | 'error';
      image_url?: string;
      polling_url?: string;
    }>('/render/images', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Check image generation status
  getImageStatus: async (imageId: string) => {
    const response = await fetchApi<{
      id: string;
      status: 'queued' | 'finished' | 'error';
      image_url?: string;
    }>(`/render/images/${imageId}`);
    return response.data;
  },

  // Get all generated images
  getImages: async (params?: {
    template_uuid?: string;
    limit?: number;
    page?: number;
  }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ images: any[]; total: number }>(`/render/images${query}`);
    return response.data || { images: [], total: 0 };
  },

  // Get collections
  getCollections: async () => {
    const response = await fetchApi<{ collections: any[] }>('/render/collections');
    return response.data?.collections || [];
  },

  // Create collection
  createCollection: async (title: string) => {
    const response = await fetchApi<{ collection: any }>('/render/collections', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
    return response.data?.collection;
  },

  // AI: Parse user request for image generation
  parseRequest: async (request: string, availableTemplates: any[]) => {
    const response = await fetchApi<{
      template_id: string;
      layers: Record<string, any>;
      confidence: number;
      message: string;
    }>('/render/ai/parse', {
      method: 'POST',
      body: JSON.stringify({ request, availableTemplates }),
    });
    return response.data;
  },

  // Get projects
  getProjects: async () => {
    const response = await fetchApi<{ projects: any[] }>('/render/projects');
    return response.data?.projects || [];
  },

  // Create project
  createProject: async (data: {
    name: string;
    description?: string;
    templates?: string[];
  }) => {
    const response = await fetchApi<{ project: any }>('/render/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data?.project;
  },

  // Update project
  updateProject: async (projectId: string, updates: any) => {
    const response = await fetchApi<{ project: any }>(`/render/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.data?.project;
  },

  // Delete project
  deleteProject: async (projectId: string) => {
    return fetchApi(`/render/projects/${projectId}`, { method: 'DELETE' });
  },

  // Get Editor SDK access token
  getEditorToken: async (options?: {
    template_uuid?: string;
    scopes?: string[];
    expires_in?: number;
    editor_options?: {
      enableCanvasResizing?: boolean;
      enableCanvasTransparency?: boolean;
      enableTemplateRenaming?: boolean;
      enableButtonSave?: boolean;
      enableButtonSaveAndClose?: boolean;
      enableHistory?: boolean;
      enableTestMode?: boolean;
      editorMode?: 'inline' | 'fullscreen';
    };
  }) => {
    const response = await fetchApi<{
      access_token: string;
      expires_at: number;
    }>('/render/editor/token', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
    return response.data;
  },

  // Upload file to Supabase Storage
  uploadFile: async (file: File, folder?: string) => {
    // Convert file to base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await fetchApi<{
      path: string;
      url: string;
      file_name: string;
      file_type: string;
      size: number;
      uploaded_at: string;
    }>('/render/upload', {
      method: 'POST',
      body: JSON.stringify({
        file_data: base64,
        file_name: file.name,
        file_type: file.type,
        folder,
      }),
    });
    return response.data;
  },

  // List files from Supabase Storage
  listFiles: async (folder?: string, limit?: number, offset?: number) => {
    const query = new URLSearchParams();
    if (folder) query.append('folder', folder);
    if (limit) query.append('limit', limit.toString());
    if (offset) query.append('offset', offset.toString());

    const response = await fetchApi<{
      files: Array<{
        name: string;
        url: string;
        id: string;
        created_at: string;
        metadata: any;
      }>;
      total: number;
    }>(`/render/files?${query.toString()}`);
    return response.data;
  },

  // Delete file from Supabase Storage
  deleteFile: async (filePath: string) => {
    const response = await fetchApi(`/render/files/${encodeURIComponent(filePath)}`, {
      method: 'DELETE',
    });
    return response;
  },

  // Get Placid credits info
  getCredits: async () => {
    const response = await fetchApi<{
      total: number;
      used: number;
      remaining: number;
      plan: string;
      note: string;
    }>('/render/credits');
    return response.data;
  },

  // Update Placid credits
  updateCredits: async (data: { used?: number; total?: number; plan?: string }) => {
    const response = await fetchApi('/render/credits/update', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  // Get render history with user names
  getHistory: async (limit?: number, offset?: number) => {
    const query = new URLSearchParams();
    if (limit) query.append('limit', limit.toString());
    if (offset) query.append('offset', offset.toString());

    const response = await fetchApi<{
      history: Array<{
        id: string;
        placid_image_id: string;
        template_uuid: string;
        layers: string;
        status: string;
        image_url: string | null;
        user_id: string | null;
        user_name: string;
        created_at: string;
        updated_at: string | null;
      }>;
      total: number;
    }>(`/render/history?${query.toString()}`);
    return response.data;
  },
};

// ============================================
// CAPTIONS API
// ============================================
export const captionsApi = {
  // Get all companies with flow data
  getFlow: async () => {
    const response = await fetchApi<any[]>('/captions/flow');
    return response.data;
  },

  // Get company flow details
  getCompanyFlow: async (companyId: string) => {
    const response = await fetchApi<any>(`/captions/flow/${companyId}`);
    return response.data;
  },

  // Companies
  getCompanies: async () => {
    const response = await fetchApi<any[]>('/captions/companies');
    return response.data;
  },

  getCompany: async (id: string) => {
    const response = await fetchApi<any>(`/captions/companies/${id}`);
    return response.data;
  },

  createCompany: async (data: any) => {
    const response = await fetchApi<any>('/captions/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  updateCompany: async (id: string, data: any) => {
    const response = await fetchApi<any>(`/captions/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteCompany: async (id: string) => {
    return fetchApi(`/captions/companies/${id}`, { method: 'DELETE' });
  },

  // Categories
  getCategories: async (companyId?: string) => {
    const query = companyId ? `?companyId=${companyId}` : '';
    const response = await fetchApi<any[]>(`/captions/categories${query}`);
    return response.data;
  },

  getCategory: async (id: string) => {
    const response = await fetchApi<any>(`/captions/categories/${id}`);
    return response.data;
  },

  createCategory: async (data: any) => {
    const response = await fetchApi<any>('/captions/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  updateCategory: async (id: string, data: any) => {
    const response = await fetchApi<any>(`/captions/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteCategory: async (id: string) => {
    return fetchApi(`/captions/categories/${id}`, { method: 'DELETE' });
  },

  // Styles
  getStyles: async (categoryId?: string) => {
    const query = categoryId ? `?categoryId=${categoryId}` : '';
    const response = await fetchApi<any[]>(`/captions/styles${query}`);
    return response.data;
  },

  getStyle: async (id: string) => {
    const response = await fetchApi<any>(`/captions/styles/${id}`);
    return response.data;
  },

  createStyle: async (data: any) => {
    const response = await fetchApi<any>('/captions/styles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  updateStyle: async (id: string, data: any) => {
    const response = await fetchApi<any>(`/captions/styles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteStyle: async (id: string) => {
    return fetchApi(`/captions/styles/${id}`, { method: 'DELETE' });
  },

  // Category-Style Assignments
  getCategoryStyles: async (categoryId: string) => {
    const response = await fetchApi<any[]>(`/captions/categories/${categoryId}/styles`);
    return response.data;
  },

  assignStyleToCategory: async (categoryId: string, styleId: string, isDefault: boolean = false) => {
    const response = await fetchApi<any>(`/captions/categories/${categoryId}/styles/${styleId}`, {
      method: 'POST',
      body: JSON.stringify({ is_default: isDefault }),
    });
    return response.data;
  },

  unassignStyleFromCategory: async (categoryId: string, styleId: string) => {
    return fetchApi(`/captions/categories/${categoryId}/styles/${styleId}`, { method: 'DELETE' });
  },

  setDefaultStyleForCategory: async (categoryId: string, styleId: string) => {
    return fetchApi(`/captions/categories/${categoryId}/styles/${styleId}/default`, { method: 'PUT' });
  },

  // Samples
  getSamples: async (styleId?: string) => {
    const query = styleId ? `?styleId=${styleId}` : '';
    const response = await fetchApi<any[]>(`/captions/samples${query}`);
    return response.data;
  },

  createSample: async (data: any) => {
    const response = await fetchApi<any>('/captions/samples', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  updateSample: async (id: string, data: any) => {
    const response = await fetchApi<any>(`/captions/samples/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteSample: async (id: string) => {
    return fetchApi(`/captions/samples/${id}`, { method: 'DELETE' });
  },

  // Generate caption
  generateCaption: async (data: {
    companyId?: string;
    companyName?: string;
    categoryId?: string;
    categoryName?: string;
    styleId?: string;
    styleName?: string;
    prompt: string;
    context?: string;
  }) => {
    const response = await fetchApi<{
      caption: string;
      company: string;
      category: string;
      style: string;
      generationId: string;
    }>('/captions/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data!;
  },
};

// ============================================
// SCRIPTS API
// ============================================
export const scriptsApi = {
  // Get all scripts
  getScripts: async () => {
    const response = await fetchApi<any[]>('/scripts');
    return response.data;
  },

  // Get script by ID
  getScript: async (id: string) => {
    const response = await fetchApi<any>(`/scripts/${id}`);
    return response.data;
  },

  // Create script manually
  createScript: async (data: any) => {
    const response = await fetchApi<any>('/scripts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Upload script from file (PDF/Text/Word)
  uploadScript: async (file: File, title?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);

    const token = getAuthToken();
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:24075/api';

    const response = await fetch(`${API_BASE}/scripts/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    const result = await response.json();
    return result.data;
  },

  // Upload multiple files
  uploadMultipleScripts: async (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const token = getAuthToken();
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:24075/api';

    const response = await fetch(`${API_BASE}/scripts/upload-multiple`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    const result = await response.json();
    return result.data;
  },

  // Update script
  updateScript: async (id: string, data: any) => {
    const response = await fetchApi<any>(`/scripts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Delete script
  deleteScript: async (id: string) => {
    return fetchApi(`/scripts/${id}`, { method: 'DELETE' });
  },

  // Generate script with AI
  generateScript: async (data: {
    prompt: string;
    wordCount?: number;
    language?: 'ar' | 'en' | 'auto';
  }) => {
    const response = await fetchApi<{
      script: string;
      wordCount: number;
      generationId: string;
    }>('/scripts/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data!;
  },

  // Get generation history (all users by default)
  getGenerationHistory: async (limit?: number, allUsers: boolean = true) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (allUsers) params.append('all', 'true');
    const queryString = params.toString();
    const response = await fetchApi<any[]>(`/scripts/history/generations${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  // Save generation to training
  saveGenerationToTraining: async (generationId: string, title: string) => {
    const response = await fetchApi<any>('/scripts/history/save-to-training', {
      method: 'POST',
      body: JSON.stringify({ generationId, title }),
    });
    return response.data;
  },
};

// ============================================
// GOOGLE DRIVE API
// ============================================
export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  createdTime?: string;
  modifiedTime?: string;
  iconLink?: string;
  thumbnailLink?: string;
  webViewLink?: string;
  webContentLink?: string;
  parents?: string[];
  shared?: boolean;
  starred?: boolean;
  trashed?: boolean;
  owners?: Array<{ displayName: string; emailAddress: string; photoLink?: string }>;
  sharingUser?: { displayName: string; emailAddress: string };
  capabilities?: {
    canEdit?: boolean;
    canDelete?: boolean;
    canShare?: boolean;
    canDownload?: boolean;
  };
}

export interface SharedDrive {
  id: string;
  name: string;
  colorRgb?: string;
  backgroundImageLink?: string;
}

export interface DriveQuota {
  limit: string;
  usage: string;
  usageInDrive: string;
  usageInTrash: string;
}

export const driveApi = {
  // Get storage quota
  getQuota: async (): Promise<DriveQuota | null> => {
    const response = await fetchApi<DriveQuota>('/drive/quota');
    return response.data || null;
  },

  // List files in My Drive or folder
  listFiles: async (options?: {
    folderId?: string;
    pageToken?: string;
    pageSize?: number;
    query?: string;
    orderBy?: string;
    starred?: boolean;
    trashed?: boolean;
  }): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams();
    if (options?.folderId) query.append('folderId', options.folderId);
    if (options?.pageToken) query.append('pageToken', options.pageToken);
    if (options?.pageSize) query.append('pageSize', options.pageSize.toString());
    if (options?.query) query.append('query', options.query);
    if (options?.orderBy) query.append('orderBy', options.orderBy);
    if (options?.starred) query.append('starred', 'true');
    if (options?.trashed) query.append('trashed', 'true');

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/files?${query.toString()}`);
    return response.data || { files: [] };
  },

  // Get recent files
  getRecent: async (pageSize?: number): Promise<DriveFile[]> => {
    const query = pageSize ? `?pageSize=${pageSize}` : '';
    const response = await fetchApi<DriveFile[]>(`/drive/recent${query}`);
    return response.data || [];
  },

  // Get starred files
  getStarred: async (pageToken?: string, pageSize?: number): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams();
    if (pageToken) query.append('pageToken', pageToken);
    if (pageSize) query.append('pageSize', pageSize.toString());

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/starred?${query.toString()}`);
    return response.data || { files: [] };
  },

  // Get trashed files
  getTrash: async (pageToken?: string, pageSize?: number): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams();
    if (pageToken) query.append('pageToken', pageToken);
    if (pageSize) query.append('pageSize', pageSize.toString());

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/trash?${query.toString()}`);
    return response.data || { files: [] };
  },

  // Get shared with me
  getShared: async (pageToken?: string, pageSize?: number): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams();
    if (pageToken) query.append('pageToken', pageToken);
    if (pageSize) query.append('pageSize', pageSize.toString());

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/shared?${query.toString()}`);
    return response.data || { files: [] };
  },

  // List shared drives
  getSharedDrives: async (pageToken?: string): Promise<{ drives: SharedDrive[]; nextPageToken?: string }> => {
    const query = pageToken ? `?pageToken=${pageToken}` : '';
    const response = await fetchApi<{ drives: SharedDrive[]; nextPageToken?: string }>(`/drive/shared-drives${query}`);
    return response.data || { drives: [] };
  },

  // List files in shared drive
  getSharedDriveFiles: async (driveId: string, options?: {
    folderId?: string;
    pageToken?: string;
    pageSize?: number;
  }): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams();
    if (options?.folderId) query.append('folderId', options.folderId);
    if (options?.pageToken) query.append('pageToken', options.pageToken);
    if (options?.pageSize) query.append('pageSize', options.pageSize.toString());

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/shared-drives/${driveId}/files?${query.toString()}`);
    return response.data || { files: [] };
  },

  // Search files
  search: async (q: string, options?: {
    pageToken?: string;
    pageSize?: number;
    mimeType?: string;
  }): Promise<{ files: DriveFile[]; nextPageToken?: string }> => {
    const query = new URLSearchParams({ q });
    if (options?.pageToken) query.append('pageToken', options.pageToken);
    if (options?.pageSize) query.append('pageSize', options.pageSize.toString());
    if (options?.mimeType) query.append('mimeType', options.mimeType);

    const response = await fetchApi<{ files: DriveFile[]; nextPageToken?: string }>(`/drive/search?${query.toString()}`);
    return response.data || { files: [] };
  },

  // Get single file
  getFile: async (fileId: string): Promise<DriveFile | null> => {
    const response = await fetchApi<DriveFile>(`/drive/files/${fileId}`);
    return response.data || null;
  },

  // Get download URL
  getDownloadUrl: async (fileId: string): Promise<string | null> => {
    const response = await fetchApi<{ url: string }>(`/drive/files/${fileId}/download-url`);
    return response.data?.url || null;
  },

  // Create folder
  createFolder: async (name: string, parentId?: string): Promise<DriveFile | null> => {
    const response = await fetchApi<DriveFile>('/drive/folders', {
      method: 'POST',
      body: JSON.stringify({ name, parentId }),
    });
    return response.data || null;
  },

  // Upload file
  uploadFile: async (file: File, parentId?: string, onProgress?: (progress: number) => void): Promise<DriveFile | null> => {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId) formData.append('parentId', parentId);

    let token = getAuthToken();
    if (!token && supabaseTokenGetter) {
      token = await supabaseTokenGetter();
    }

    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.data || null);
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));

      xhr.open('POST', `${API_BASE_URL}/drive/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  },

  // Rename file
  rename: async (fileId: string, name: string): Promise<DriveFile | null> => {
    const response = await fetchApi<DriveFile>(`/drive/files/${fileId}/rename`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    return response.data || null;
  },

  // Move file
  move: async (fileId: string, parentId: string): Promise<DriveFile | null> => {
    const response = await fetchApi<DriveFile>(`/drive/files/${fileId}/move`, {
      method: 'PATCH',
      body: JSON.stringify({ parentId }),
    });
    return response.data || null;
  },

  // Toggle star
  toggleStar: async (fileId: string, starred: boolean): Promise<boolean> => {
    const response = await fetchApi<{ success: boolean }>(`/drive/files/${fileId}/star`, {
      method: 'PATCH',
      body: JSON.stringify({ starred }),
    });
    return response.success;
  },

  // Copy file
  copy: async (fileId: string, name?: string, parentId?: string): Promise<DriveFile | null> => {
    const response = await fetchApi<DriveFile>(`/drive/files/${fileId}/copy`, {
      method: 'POST',
      body: JSON.stringify({ name, parentId }),
    });
    return response.data || null;
  },

  // Delete file (move to trash)
  delete: async (fileId: string, permanent?: boolean): Promise<boolean> => {
    const query = permanent ? '?permanent=true' : '';
    const response = await fetchApi<{ success: boolean }>(`/drive/files/${fileId}${query}`, {
      method: 'DELETE',
    });
    return response.success;
  },

  // Restore file from trash
  restore: async (fileId: string): Promise<boolean> => {
    const response = await fetchApi<{ success: boolean }>(`/drive/files/${fileId}/restore`, {
      method: 'POST',
    });
    return response.success;
  },

  // AI-powered search - understands natural language queries
  aiSearch: async (query: string, conversationHistory?: Array<{ role: string; content: string }>): Promise<{
    message: string;
    bestMatch: (DriveFile & { confidence: number; shouldOpenAutomatically: boolean }) | null;
    files: DriveFile[];
    totalFiles?: number;
    searchConfig: {
      searchTerms: string[];
      alternativeSearchTerms?: string[];
      mimeType: string | null;
      searchStarred: boolean;
      searchRecent: boolean;
      searchShared: boolean;
      searchSharedDrives?: boolean;
      yearFilter?: string | null;
      monthFilter?: string | null;
      userFriendlyResponse: string;
    };
    followUpQuestion?: string | null;
  }> => {
    const response = await fetchApi<{
      message: string;
      bestMatch: (DriveFile & { confidence: number; shouldOpenAutomatically: boolean }) | null;
      files: DriveFile[];
      totalFiles?: number;
      searchConfig: any;
      followUpQuestion?: string | null;
    }>('/drive/ai-search', {
      method: 'POST',
      body: JSON.stringify({ query, conversationHistory }),
    });
    return response.data || { message: 'Search error', bestMatch: null, files: [], totalFiles: 0, searchConfig: {} as any };
  },
};

// ============================================
// TRIGGERS API (Super Admin Only)
// ============================================
export interface TriggerAction {
  action_type: 'email' | 'whatsapp' | 'slack' | 'notification';
  config: Record<string, any>;
}

export interface Trigger {
  id: string;
  name: string;
  description?: string;
  schedule_type: 'daily' | 'weekly' | 'monthly' | 'cron';
  schedule_days: number[];
  schedule_time: string;
  schedule_cron?: string;
  timezone: string;
  target_type: 'all' | 'specific' | 'by_role';
  target_users: string[];
  target_roles: string[];
  use_ai_message: boolean;
  ai_prompt?: string;
  fallback_template?: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_run_at?: string;
  next_run_at?: string;
  actions?: TriggerAction[];
}

export interface TriggerLog {
  id: string;
  trigger_id: string;
  started_at: string;
  completed_at?: string;
  status: 'running' | 'completed' | 'failed' | 'partial';
  total_users: number;
  successful_count: number;
  failed_count: number;
  error_message?: string;
}

export const triggersApi = {
  // Get all triggers
  getAll: async (): Promise<Trigger[]> => {
    const response = await fetchApi<{ triggers: Trigger[] }>('/triggers');
    return response.data?.triggers || [];
  },

  // Get single trigger
  getById: async (id: string): Promise<Trigger | null> => {
    const response = await fetchApi<{ trigger: Trigger }>(`/triggers/${id}`);
    return response.data?.trigger || null;
  },

  // Create trigger
  create: async (data: {
    name: string;
    description?: string;
    schedule_type: 'daily' | 'weekly' | 'monthly' | 'cron';
    schedule_days?: number[];
    schedule_time: string;
    timezone?: string;
    target_type: 'all' | 'specific' | 'by_role';
    target_users?: string[];
    target_roles?: string[];
    use_ai_message?: boolean;
    ai_prompt?: string;
    fallback_template?: string;
    actions: TriggerAction[];
  }): Promise<Trigger | null> => {
    const response = await fetchApi<{ trigger: Trigger }>('/triggers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data?.trigger || null;
  },

  // Update trigger
  update: async (id: string, data: Partial<{
    name: string;
    description?: string;
    schedule_type: 'daily' | 'weekly' | 'monthly' | 'cron';
    schedule_days?: number[];
    schedule_time: string;
    timezone?: string;
    target_type: 'all' | 'specific' | 'by_role';
    target_users?: string[];
    target_roles?: string[];
    use_ai_message?: boolean;
    ai_prompt?: string;
    fallback_template?: string;
    actions?: TriggerAction[];
  }>): Promise<Trigger | null> => {
    const response = await fetchApi<{ trigger: Trigger }>(`/triggers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data?.trigger || null;
  },

  // Delete trigger
  delete: async (id: string): Promise<boolean> => {
    const response = await fetchApi(`/triggers/${id}`, { method: 'DELETE' });
    return response.success;
  },

  // Toggle trigger active status
  toggle: async (id: string, isActive: boolean): Promise<Trigger | null> => {
    const response = await fetchApi<{ trigger: Trigger }>(`/triggers/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    });
    return response.data?.trigger || null;
  },

  // Execute trigger manually
  execute: async (id: string): Promise<TriggerLog | null> => {
    const response = await fetchApi<{ log: TriggerLog }>(`/triggers/${id}/execute`, {
      method: 'POST',
    });
    return response.data?.log || null;
  },

  // Get trigger logs
  getLogs: async (triggerId: string, limit?: number): Promise<TriggerLog[]> => {
    const query = limit ? `?limit=${limit}` : '';
    const response = await fetchApi<{ logs: TriggerLog[] }>(`/triggers/${triggerId}/logs${query}`);
    return response.data?.logs || [];
  },

  // Get user logs for execution
  getUserLogs: async (logId: string): Promise<any[]> => {
    const response = await fetchApi<{ userLogs: any[] }>(`/triggers/logs/${logId}/users`);
    return response.data?.userLogs || [];
  },

  // Get user context (tasks, meetings, etc.)
  getUserContext: async (userId: string): Promise<{
    user: { id: string; name: string; email: string; phone?: string; role?: string };
    context: Record<string, any>;
  } | null> => {
    const response = await fetchApi<{
      user: { id: string; name: string; email: string; phone?: string; role?: string };
      context: Record<string, any>;
    }>(`/triggers/preview/user/${userId}/context`);
    return response.data || null;
  },

  // Preview AI message for a user
  previewMessage: async (userId: string, aiPrompt: string): Promise<{
    user: { id: string; name: string; email: string };
    context: Record<string, any>;
    generatedMessage: string;
  } | null> => {
    const response = await fetchApi<{
      user: { id: string; name: string; email: string };
      context: Record<string, any>;
      generatedMessage: string;
    }>('/triggers/preview/message', {
      method: 'POST',
      body: JSON.stringify({ userId, aiPrompt }),
    });
    return response.data || null;
  },
};

// ============================================
// AUDOME API (Image/Video Generation)
// ============================================
export const audomeApi = {
  // Test Audome API connection
  testConnection: async () => {
    const response = await fetchApi<{ success: boolean; message: string }>('/audome/test-connection');
    return response.data || { success: false, message: 'Unknown error' };
  },

  // Get embed token for Audome Studio
  getEmbedToken: async () => {
    const response = await fetchApi<{ token: string }>('/audome/embed-token');
    return response.data;
  },

  // Get all templates
  getTemplates: async (params?: {
    project_id?: string;
    search?: string;
    type?: 'image' | 'video';
    limit?: number;
    page?: number;
  }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ templates: any[]; total: number; page: number }>(`/audome/templates${query}`);
    return response.data || { templates: [], total: 0, page: 1 };
  },

  // Get single template
  getTemplate: async (templateId: string) => {
    const response = await fetchApi<{ template: any }>(`/audome/templates/${templateId}`);
    return response.data?.template;
  },

  // Create render (generate image/video)
  createRender: async (data: {
    template_id: string;
    variables: Record<string, any>;
    webhook_url?: string;
  }) => {
    const response = await fetchApi<any>('/audome/renders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Get render status
  getRenderStatus: async (renderId: string) => {
    const response = await fetchApi<any>(`/audome/renders/${renderId}`);
    return response.data;
  },

  // Get render history from Audome
  getRenderHistory: async (params?: {
    template_id?: string;
    status?: string;
    limit?: number;
    page?: number;
  }) => {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await fetchApi<{ renders: any[]; total: number }>(`/audome/renders${query}`);
    return response.data || { renders: [], total: 0 };
  },

  // Get local history with user names
  getHistory: async (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    const response = await fetchApi<{ history: any[]; total: number }>(`/audome/history${query}`);
    return response.data || { history: [], total: 0 };
  },

  // Get projects
  getProjects: async () => {
    const response = await fetchApi<{ projects: any[] }>('/audome/projects');
    return response.data?.projects || [];
  },

  // Create project
  createProject: async (data: { name: string; description?: string }) => {
    const response = await fetchApi<{ project: any }>('/audome/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data?.project;
  },

  // Get fonts
  getFonts: async () => {
    const response = await fetchApi<{ fonts: any[] }>('/audome/fonts');
    return response.data || { fonts: [] };
  },

  // Upload asset
  uploadAsset: async (data: { file_url: string; name?: string; type?: string }) => {
    const response = await fetchApi<{ id: string; url: string }>('/audome/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  // Upload file to storage
  uploadFile: async (file: File, folder?: string) => {
    return new Promise<{ path: string; url: string; file_name: string } | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const response = await fetchApi<{
            path: string;
            url: string;
            file_name: string;
          }>('/audome/upload', {
            method: 'POST',
            body: JSON.stringify({
              file_data: reader.result,
              file_name: file.name,
              file_type: file.type,
              folder,
            }),
          });
          resolve(response.data || null);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },
};

// Default export
export default {
  auth: authApi,
  calendar: calendarApi,
  gmail: gmailApi,
  asana: asanaApi,
  meetings: meetingsApi,
  dashboard: dashboardApi,
  voice: voiceApi,
  ai: aiApi,
  settings: settingsApi,
  users: usersApi,
  smartEmail: smartEmailApi,
  smartMeetings: smartMeetingsApi,
  preferences: preferencesApi,
  agent: agentApi,
  whatsapp: whatsappApi,
  render: renderApi,
  audome: audomeApi,
  zoho: zohoApi,
  captions: captionsApi,
  scripts: scriptsApi,
  drive: driveApi,
  triggers: triggersApi,
  setAuthToken,
  getAuthToken,
};
